import apiFetch from '@wordpress/api-fetch';
import debounceAsync from './debounce-async';
import getNewestData from '../get-newest-data';
import isPreferencesData from '../is-preferences-data';
import { writeStoredJSON } from '../local-storage';
import type {
	CreateOptions,
	PersistenceLayer,
	PreferencesData,
} from '../types';

/**
 * Creates a persistence layer that stores data in WordPress user meta via the
 * REST API.
 *
 * @param {CreateOptions}        [options]
 * @param {PreferencesData|null} [options.preloadedData]          Any persisted preferences data that should be preloaded.
 *                                                                When set, the persistence layer will avoid fetching data
 *                                                                from the REST API.
 * @param {string}               [options.localStorageRestoreKey] The key to use for restoring the localStorage backup, used
 *                                                                when the persistence layer calls `localStorage.getItem` or
 *                                                                `localStorage.setItem`.
 * @param {number}               [options.requestDebounceMS]      Debounce requests to the API so that they only occur at
 *                                                                minimum every `requestDebounceMS` milliseconds, and don't
 *                                                                swamp the server. Defaults to 2500ms.
 *
 * @return A persistence layer for WordPress user meta.
 */
export default function create( {
	preloadedData,
	localStorageRestoreKey = 'WP_PREFERENCES_RESTORE_DATA',
	requestDebounceMS = 2500,
}: CreateOptions = {} ): PersistenceLayer {
	let cache = isPreferencesData( preloadedData ) ? preloadedData : undefined;
	const debouncedApiFetch = debounceAsync( apiFetch, requestDebounceMS );

	async function get() {
		if ( cache ) {
			return cache;
		}

		const user = await apiFetch< {
			meta?: { persisted_preferences?: unknown };
		} >( {
			path: '/wp/v2/users/me?context=edit',
		} );

		cache =
			getNewestData(
				user?.meta?.persisted_preferences,
				localStorageRestoreKey
			) ?? {};

		return cache;
	}

	function set( newData: PreferencesData ) {
		const dataWithTimestamp = {
			...newData,
			_modified: new Date().toISOString(),
		};
		cache = dataWithTimestamp;

		// Store data in local storage as a fallback. If for some reason the
		// api request does not complete or becomes unavailable, this data
		// can be used to restore preferences.
		writeStoredJSON( localStorageRestoreKey, dataWithTimestamp );

		// The user meta endpoint seems susceptible to errors when consecutive
		// requests are made in quick succession. Ensure there's a gap between
		// any consecutive requests.
		//
		// Catch and do nothing with errors from the REST API.
		debouncedApiFetch( {
			path: '/wp/v2/users/me',
			method: 'PUT',
			// `keepalive` will still send the request in the background,
			// even when a browser unload event might interrupt it.
			// This should hopefully make things more resilient.
			// This does have a size limit of 64kb, but the data is usually
			// much less.
			keepalive: true,
			data: {
				meta: {
					persisted_preferences: dataWithTimestamp,
				},
			},
		} ).catch( () => {} );
	}

	return {
		get,
		set,
	};
}
